import React, { useState } from 'react';
import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import { TILE_IMAGES_FALLBACK } from '@mock/fallback';
import { boxShadowMixins, mixins } from '@styles/mixins';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import { Keyable, TripTypes } from '@model/common';
import { OfferType } from '@model/offers/offers';
import { AppVariant } from '@model/config/brands';
import { CurrencySymbolMapping } from '@model/common/currency/currency';
import { useI18NextContext } from '@components/hooks';
import { DestinationLink } from '@components/common/next-link';
import { RippleCard } from '@components/common/ripple-card';
import { ImageCrossfade } from '@components/common/image-crossfade';
import { getQueryAsString } from '@util/common';

/* ***************** *
 *       Types       *
 * ***************** */

export interface DestinationProps {
  theme?: Theme;
  testId?: string;
  path: string;
  destination: string;
  fromPrice?: number;
  tripType?: TripTypes;
  images: Array<string>;
  sizes?: string;
  renderContent?: JSX.Element;
  offerType?: OfferType;
  urlParams?: Keyable;
}

/* ***************** *
 *       Styles      *
 * ***************** */

const Container = styled.div(({ theme }: ThemeProps) => ({
  borderRadius: theme.custom.spacing.small,
  display: 'flex',
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).lighter,
  overflow: 'hidden',
  position: 'relative',
  alignItems: 'flex-end',
  padding: theme.custom.spacing.large,
  height: 255,
  cursor: 'pointer',
  backgroundColor: theme.custom.colors.white
}));

const ImageCrossfadeContainer = styled.div(() => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  borderRadius: 10
}));

export const DestinationContent = styled.div(({ theme }: ThemeProps) => ({
  width: '100%',
  padding: `calc(${theme.custom.spacing.xSmall}px * 2)`,
  backgroundColor: theme.custom.colors.overlay.dark,
  backdropFilter: 'blur(10px)',
  maxWidth: 216,
  zIndex: 2,
  borderRadius: 6
}));

export const DestinationTitleLabel = styled.h3(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  color: theme.custom.colors.white,
  margin: 0,
  ...mixins(theme).truncate,
  fontWeight: 'bold'
}));

const AttributeList = styled.ul({
  padding: 0,
  marginBottom: 0,
  marginTop: 0
});

const DestinationAttribute = styled.li({
  display: 'flex'
});

const DestinationAttributeLabel = styled.span({});

const DestinationAttributeDescriptionLabel = styled.span(({ theme }: ThemeProps) => ({
  fontSize: '1.4rem',
  color: theme.custom.colors.white,
  ...mixins(theme).truncate
}));

const FromPrice = styled.span({
  fontSize: '1.8rem'
});

const StyledImageCrossfade = styled(ImageCrossfade)({
  borderRadius: 10,
  '> div': {
    borderRadius: 10
  }
});

/* ************************** *
 *    DestinationComponent    *
 * ************************** */
export const DestinationComponent = (props: DestinationProps) => {
  const [isHover, setIsHover] = useState(false);
  const { destination, tripType, images, path, fromPrice, renderContent, offerType, sizes, urlParams } = props;
  const t = useI18NextContext();

  const currencySymbol = CurrencySymbolMapping[AppVariant.isMercuryIreland() ? 'EUR' : 'GBP'];

  const imageUrls = images && images.length ? images : TILE_IMAGES_FALLBACK;
  const queryString = urlParams ? getQueryAsString(urlParams) : undefined;

  /* *** RENDERERS *** */
  return (
    <DestinationLink path={path} tripType={tripType} offerType={offerType} urlParams={queryString}>
      <RippleCard>
        <Container
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          data-testid={props.testId || TestId.destination.main}
        >
          <ImageCrossfadeContainer>
            <StyledImageCrossfade
              testId={TestId.destination.imageCrossfade}
              images={imageUrls}
              sizes={sizes}
              triggerPlay={isHover}
            />
          </ImageCrossfadeContainer>
          {renderContent || (
            <DestinationContent>
              <DestinationTitleLabel data-testid={TestId.destination.titleLabel}>{destination}</DestinationTitleLabel>
              <AttributeList>
                {fromPrice && (
                  <DestinationAttribute>
                    <DestinationAttributeLabel data-testid={TestId.destination.attributeLabel}>
                      <DestinationAttributeDescriptionLabel data-testid={TestId.destination.attributeDescriptionLabel}>
                        {t('common__label--from')}{' '}
                        <FromPrice>
                          {currencySymbol}
                          {fromPrice}
                        </FromPrice>{' '}
                        {t('common__per-person--label')}
                      </DestinationAttributeDescriptionLabel>
                    </DestinationAttributeLabel>
                  </DestinationAttribute>
                )}
              </AttributeList>
            </DestinationContent>
          )}
        </Container>
      </RippleCard>
    </DestinationLink>
  );
};

export const DestinationTile = withTheme(DestinationComponent);
